import {Card, Flag, FormField, type FormControl} from 'platform/components';
import {Grid, Show, Text, VStack} from 'platform/foundation';

import {useWatch, type UseFormReturn} from 'react-hook-form';

import type {
  ConfiguratorBundleResponseBody,
  EvaluateConfigurationResponseBody,
} from '@dms/api/new-vehicle';
import {
  BasisPrice,
  getNewVehicleEnumLabel,
  PriceBreakdown,
  SelectableCard,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';

import {ColourSwatch} from '../../../../components/ColourSwatch/ColourSwatch';
import type {ConfiguratorFormValues} from '../../NewVehicleConfigurator';
import {getEvaluationLineLabel} from '../../utils/newVehicleLabels';
import {isCompleteProposedDiscount} from '../../utils/proposedDiscount';
import {SectionLabel, StepIntro} from '../StepIntro';

export type LookAndSummaryProps = {
  bundle?: ConfiguratorBundleResponseBody;
  trimId: string;
  form?: UseFormReturn<ConfiguratorFormValues>;
  control: FormControl<ConfiguratorFormValues>;
  evaluation?: EvaluateConfigurationResponseBody;
  /** The current selection's offers; undefined while its evaluation is pending. */
  offers?: EvaluateConfigurationResponseBody['offers'];
  isFetching: boolean;
  isSettled: boolean;
  canViewCost: boolean;
  canSelect: boolean;
  canWrite: boolean;
  factoryFreezeReason?: string;
};

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});
const tWithValues = (key: string, options: {defaultValue: string; [value: string]: string}) =>
  i18n.t(key, options);
const INCLUDED_PRICE = '0.00';

export function LookAndSummary(props: LookAndSummaryProps) {
  const colourId = useWatch({control: props.control, name: 'colourId'});
  const upholsteryId = useWatch({control: props.control, name: 'upholsteryId'});
  const selectedTrim = props.bundle?.trims.find(({id}) => id === props.trimId);
  const evaluationById = new Map(props.evaluation?.items.map((item) => [item.id, item]));
  const colourOffer = (id: string) =>
    props.offers?.find((offer) => offer.type === 'colour' && offer.id === id);
  const discountKind = useWatch({control: props.control, name: 'proposedDiscount.kind'});
  const discountValue = useWatch({control: props.control, name: 'proposedDiscount.value'});
  const discountBasis = useWatch({control: props.control, name: 'proposedDiscount.basis'});
  const priceEvaluation = props.evaluation;
  const hasIncompleteDiscountIntent =
    Boolean(discountKind || discountValue || discountBasis) &&
    !isCompleteProposedDiscount({kind: discountKind, value: discountValue, basis: discountBasis});
  const currency = props.bundle?.currency ?? '';
  const proposedDiscountLabel =
    discountKind && discountValue && discountBasis
      ? tWithValues('entity.newVehicle.labels.proposedDiscountSummary', {
          defaultValue: 'Proposed discount: {value} {kind} of {basis}',
          value: discountValue,
          kind: discountKind === 'percent' ? '%' : currency,
          basis: getNewVehicleEnumLabel('discountBasis', discountBasis, t).toLowerCase(),
        })
      : undefined;

  return (
    <VStack spacing={6}>
      <StepIntro
        title={t('entity.newVehicle.steps.lookTitle', 'Step 4 — Colours & upholstery')}
        description={t(
          'entity.newVehicle.steps.lookIntro',
          'Choose paint and interior. Combinations the factory does not build for this trim are greyed out.'
        )}
      />
      <VStack spacing={3}>
        <SectionLabel
          label={t('entity.newVehicle.labels.exteriorPaint', 'Exterior paint')}
          count={props.bundle?.colours.length ?? 0}
        />
        <Grid columns={[1, 2, 3, 4]} spacing={3}>
          <Show when={props.bundle}>
            {(bundle) =>
              bundle.colours.map((colour) => {
                const result = evaluationById.get(colour.id);
                // Trim availability fails closed (BE D57), so grey it out before the evaluator drops it.
                const offTrim = !colour.trims.some(({trimId}) => trimId === props.trimId);
                const unavailable = offTrim || result?.availability === 'unavailable';
                const disabled = Boolean(props.factoryFreezeReason) || unavailable;
                const offer = colourOffer(colour.id);
                return (
                  <SelectableCard
                    key={colour.id}
                    control="radio"
                    name="colour"
                    title={colour.name}
                    isSelected={colour.id === colourId}
                    isDisabled={!props.canSelect || disabled}
                    isRequired
                    reason={
                      props.factoryFreezeReason ??
                      (offTrim // eslint-disable-line no-nested-ternary -- Pre-existing violation
                        ? tWithValues('entity.newVehicle.labels.notAvailableForTrim', {
                            defaultValue: 'Not available for {trim}',
                            trim:
                              selectedTrim?.name ??
                              t('entity.newVehicle.labels.trimLevel', 'Trim level'),
                          })
                        : unavailable
                          ? (result?.reason ?? undefined)
                          : undefined)
                    }
                    onSelect={() =>
                      props.form?.setValue('colourId', colour.id, {shouldDirty: true})
                    }
                    leading={<ColourSwatch name={colour.name} hex={colour.hex} size="tile" />}
                    heading={
                      <Text size="small" alternative color={disabled ? 'secondary' : 'primary'}>
                        {colour.name}
                      </Text>
                    }
                    description={
                      <Show when={offer}>
                        {(price) => (
                          <BasisPrice
                            net={price.priceNet}
                            gross={price.priceGross}
                            basis={bundle.priceBasis}
                            currency={currency}
                            prefix="+"
                            size="small"
                            showIncludedWhenZero
                            noWrap
                            color="secondary"
                          />
                        )}
                      </Show>
                    }
                  />
                );
              })
            }
          </Show>
        </Grid>
      </VStack>
      <VStack spacing={3}>
        <SectionLabel
          label={t('entity.newVehicle.labels.interior', 'Interior')}
          count={props.bundle?.upholsteries.length ?? 0}
        />
        <Grid columns={[1, 2, 3, 4]} spacing={3}>
          <Show when={props.bundle}>
            {(bundle) =>
              bundle.upholsteries.map((upholstery) => {
                const available = upholstery.availableForTrimIds.includes(props.trimId);
                const restricted = props.bundle?.colourUpholsteryRestrictions.some(
                  (restriction) =>
                    restriction.colourId === colourId && restriction.upholsteryId === upholstery.id
                );
                const disabled = Boolean(props.factoryFreezeReason) || !available || restricted;
                const reason =
                  props.factoryFreezeReason ??
                  (!available // eslint-disable-line no-nested-ternary -- Pre-existing violation
                    ? tWithValues('entity.newVehicle.labels.notAvailableForTrim', {
                        defaultValue: 'Not available for {trim}',
                        trim:
                          selectedTrim?.name ??
                          t('entity.newVehicle.labels.trimLevel', 'Trim level'),
                      })
                    : restricted
                      ? t(
                          'entity.newVehicle.labels.notAvailableWithColour',
                          'Not available with the selected colour'
                        )
                      : evaluationById.get(upholstery.id)?.reason);
                return (
                  <SelectableCard
                    key={upholstery.id}
                    control="radio"
                    name="upholstery"
                    title={upholstery.name}
                    isSelected={upholstery.id === upholsteryId}
                    isDisabled={!props.canSelect || disabled}
                    isRequired
                    reason={disabled ? (reason ?? undefined) : undefined}
                    onSelect={() =>
                      props.form?.setValue('upholsteryId', upholstery.id, {shouldDirty: true})
                    }
                    leading={
                      <ColourSwatch
                        name={upholstery.name}
                        hex={upholstery.interiorColorHex}
                        size="tile"
                      />
                    }
                    heading={
                      <Text size="small" alternative color={disabled ? 'secondary' : 'primary'}>
                        {upholstery.name}
                      </Text>
                    }
                    description={
                      <BasisPrice
                        net={upholstery.priceNet}
                        gross={upholstery.priceGross}
                        basis={bundle.priceBasis}
                        currency={currency}
                        prefix="+"
                        size="small"
                        showIncludedWhenZero
                        noWrap
                        color="secondary"
                      />
                    }
                  />
                );
              })
            }
          </Show>
        </Grid>
      </VStack>
      <Card
        isExpandable
        isClosedByDefault={Boolean(props.form)}
        variant="inlineGrey"
        title={t('entity.newVehicle.labels.salesTools', 'Sales tools')}
        subtitle={t(
          'entity.newVehicle.labels.proposedDiscountHint',
          'proposed discount · visible to sales roles only'
        )}
      >
        <VStack spacing={4}>
          <Grid columns={[1, 3, 3, 3]} spacing={3}>
            <FormField
              control={props.control}
              type="choice"
              name="proposedDiscount.kind"
              isDisabled={!props.canWrite}
              label={t('entity.newVehicle.labels.discountKind', 'Proposed discount type')}
              options={['amount', 'percent'].map((value) => ({
                value,
                label: getNewVehicleEnumLabel('discountKind', value, t),
              }))}
            />
            <FormField
              control={props.control}
              type="text"
              name="proposedDiscount.value"
              isDisabled={!props.canWrite}
              label={t('entity.newVehicle.labels.discountValue', 'Proposed discount value')}
            />
            <FormField
              control={props.control}
              type="choice"
              name="proposedDiscount.basis"
              isDisabled={!props.canWrite}
              label={t('entity.newVehicle.labels.discountBasis', 'Discount basis')}
              options={['net', 'gross'].map((value) => ({
                value,
                label: getNewVehicleEnumLabel('discountBasis', value, t),
              }))}
            />
          </Grid>
          <Show when={hasIncompleteDiscountIntent}>
            <Text size="small" color="danger">
              {t(
                'entity.newVehicle.notifications.completeProposedDiscount',
                'Complete all proposed discount fields'
              )}
            </Text>
          </Show>
          <Show when={proposedDiscountLabel}>
            <Flag label={proposedDiscountLabel ?? ''} colorScheme="blue" isSubtle />
          </Show>
          <PriceBreakdown
            basis={props.bundle?.priceBasis}
            currency={currency}
            lines={priceEvaluation?.items
              .filter((item) => item.priceNet !== null)
              .map((item) => ({
                id: `${item.type}-${item.id}`,
                label: props.bundle
                  ? getEvaluationLineLabel(item, props.bundle, t)
                  : t('entity.newVehicle.labels.unknownItem', 'Unknown item'),
                amountNet: item.priceNet ?? INCLUDED_PRICE,
                amountGross: item.priceGross ?? INCLUDED_PRICE,
              }))}
            totals={priceEvaluation?.totals}
            proposed={{
              net: priceEvaluation?.proposedSellingPriceNet ?? undefined,
              gross: priceEvaluation?.proposedSellingPriceGross ?? undefined,
              discountLabel: proposedDiscountLabel,
            }}
            cost={
              props.canViewCost
                ? {costNet: priceEvaluation?.totals.costNet ?? undefined}
                : undefined
            }
            canViewCost={props.canViewCost}
            isPending={props.isFetching || !props.isSettled}
            vatRateLabel={
              props.bundle
                ? i18n.t('entity.newVehicle.labels.vatRate', {
                    defaultValue: 'VAT {rate} %',
                    rate: props.bundle.vatRate,
                  })
                : undefined
            }
          />
        </VStack>
      </Card>
    </VStack>
  );
}
